<script lang="ts" setup generic="TComponent extends new (...args: any) => any, TModelValue">
import { ButtonComp, IconComp, ModalComp } from '@/components';
import { onMounted, ref, unref, type ComponentInstance } from 'vue';
import { computed } from 'vue';
import ContentComp from '../layout/ContentComp.vue';
import { ModalButton } from './ModalButton';
import type { IDialogSystemModalInstanceExposed } from './IDialogSystemModalInstanceExposed';
import { type IDialogInstance } from '@/composeables/dialog/IDialogInstance';
import type { IDialogClosecallbackParams } from '@/composeables/dialog/IDialogClosecallbackParams';
import type { TaskLoaderState } from '@/composeables';
import { $ti } from '@/locize';

const props = defineProps<IDialogInstance<TComponent, TModelValue> & { modelValue?: TModelValue }>();

const emits = defineEmits<{
    (e: 'dialogFullyClosed', uniqueLoopKey: string): void;
    (e: 'escapeKeyDown', event: KeyboardEvent): void;
    (e: 'update:modelValue', value: TModelValue | undefined): void;
    (e: 'updateNamedModelValue', name: string, value: any): void;
}>();

const modaldialog = ref<ComponentInstance<typeof ModalComp>>();

const componentRef = ref<ComponentInstance<TComponent> | null>(null);

const computedBodyComponentBindings = computed(() => {
    const params: Record<string, any> = { ...(props.BodyComponentProps ?? {}) };

    // Add actual vmodel
    if (props.VModel != null) {
        // We have a vmodel, so we set it
        params['modelValue'] = unref(props.VModel);
        params['onUpdate:modelValue'] = (value: TModelValue) => emits('update:modelValue', value);
    }

    // Add named vmodels
    const namedVModels = props.NamedVModels ?? {};
    for (const key in namedVModels) {
        params[key] = unref(namedVModels[key]);
        params['onUpdate:' + key] = (value: any) => emits('updateNamedModelValue', key, value);
    }

    return params;
});

const isHtml = computed(() => {
    if (props.BodyString === undefined) {
        return false;
    }

    if (typeof props.BodyString === 'string' && props.BodyString?.includes('<') && props.BodyString?.includes('>')) {
        return true;
    }
    return false;
});

/** Passthrough for the escape button so the DialogSystemModalStack can handle it */
const escapeKeyDown = (event: KeyboardEvent) => emits('escapeKeyDown', event);

/**
 * Called when the dialog is closing due to user pressing escape or the corner X
 */
const dialogUserClosed = async () => {
    closeTheDialog(props.UserClosedAction);
};

/**
 * Calls the usersupplied action when a button is clicked.
 *
 * Then we close the dialog and resolve the promise with the result of the user action.
 * @param button The button that was clicked
 */
const dialogButtonClickedHandler = async (button: ModalButton<IDialogClosecallbackParams<any>>) => {
    const buttonLoaderState = button.props.loadstate;

    const userAction = button.action;

    closeTheDialog(userAction, buttonLoaderState);
};

const closeTheDialog = async (userAction?: (args: IDialogClosecallbackParams<any>) => any, buttonLoaderState?: TaskLoaderState | null) => {
    let userStoppedClosing = false;

    let userActionResult: any = undefined;
    if (userAction) {
        userActionResult = await userAction({
            dialogBodyComponentInstanceRef: componentRef.value ?? undefined,

            stopDialogFromClosing: () => {
                userStoppedClosing = true;
            },
        });
    }

    if (userStoppedClosing) {
        return;
    }

    // Wait for all the loading on the button
    if (buttonLoaderState?.Loaded?.length) {
        await Promise.all(buttonLoaderState?.Loaded);
    }

    if (props.LoadingState?.Loaded?.length) {
        await Promise.all(props.LoadingState?.Loaded);
    }

    // Actually close the dialog
    if (modaldialog.value) {
        await modaldialog.value?.hideAndDestroy();
    }

    // Resolve the promise of the dialog closing with the user action result
    props.PromiseResolver(userActionResult);

    // Tell the DialogSystemModalStack that we are fully closed
    emits('dialogFullyClosed', props.UniqueLoopKey);
};

if (props.OnDialogOpened) {
    onMounted(() => {
        props.OnDialogOpened?.({
            closeDialog: dialogUserClosed,
            closePromise: props.ClosePromise,
            dialogBodyComponentInstanceRef: componentRef.value,
        });
    });
}

defineExpose<IDialogSystemModalInstanceExposed>({
    requestClose: dialogUserClosed,
    UniqueLoopKey: props.UniqueLoopKey,
});
</script>
<template>
    <ModalComp
        :title="unref(Title)"
        :type="Type"
        :size="Size"
        :ignorefullscreen="true"
        :disable-animation="DisableAnimation"
        :keep-alive="false"
        :close-on-escape="false"
        :inital-visible="true"
        @escapeKeyDown="escapeKeyDown"
        ref="modaldialog"
    >
        <template #exitButton>
            <ButtonComp
                v-if="EnableHideOnEscape"
                button-type="Secondary"
                class="btn btn-dark ms-auto no-focus"
                @click.capture="dialogUserClosed()"
            >
                <IconComp icon="fa-xmark" />
            </ButtonComp>
        </template>
        <template #content>
            <ContentComp
                :height="Height"
                :min-height="MinHeight"
                :fillContent="true"
            >
                <template v-if="isHtml">
                    <div
                        v-html="BodyString"
                        :class="{
                            'text-center': TextAlign === 'center',
                            'text-right': TextAlign === 'right',
                        }"
                    />
                </template>

                <div
                    class="pt-2 flex-fill"
                    :class="{
                        'text-center': TextAlign === 'center',
                        'text-right': TextAlign === 'right',
                    }"
                    v-else
                >
                    <span
                        v-if="BodyString !== undefined && BodyStringParams === undefined"
                        v-html="$tm(BodyString, '', true)"
                    />
                    <span
                        v-if="BodyString !== undefined && BodyStringParams !== undefined"
                        v-html="$ti(BodyString, BodyStringParams, true)"
                    />

                    <component
                        v-else
                        :is="BodyComponent"
                        v-bind="computedBodyComponentBindings"
                        ref="componentRef"
                    />
                </div>
            </ContentComp>
        </template>
        <template #footer>
            <ButtonComp
                v-for="button in Buttons"
                :key="button.loopKey"
                v-bind="button.props"
                @click="dialogButtonClickedHandler(button)"
            >
                {{ button.text }}
            </ButtonComp>
        </template>
    </ModalComp>
</template>
